import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';

import { useFormContext } from '@/components/react-hook-form';
import { FormAwareDividerSlide } from '@/modules/slideEditor/formAware/FormAwareDividerSlide';

import {
  SlideEditorBatchFormShape,
  usePresentationTemplateSlideEditorContext,
} from './PresentationTemplateSlideEditor.context';

export interface PresentationTemplateDividerSlideProps {
  bundleId: string;
}

/**
 * Template-editor binding for a divider bundle: resolves the batch-form field
 * paths from the bundle ID and delegates form handling to the shared
 * {@link FormAwareDividerSlide}.
 */
export function PresentationTemplateDividerSlide({
  bundleId,
}: PresentationTemplateDividerSlideProps) {
  const { control } = useFormContext<SlideEditorBatchFormShape>();
  const { getBundleIndexById } = usePresentationTemplateSlideEditorContext();

  // Look up the stable index for this bundle ID to access form fields
  const bundleIndex = useMemo(
    () => getBundleIndexById(bundleId),
    [getBundleIndexById, bundleId]
  );

  const dividerPage = useWatch({
    control,
    name: `bundles.${bundleIndex}.dividerPage`,
  });

  // Early return if dividerPage data isn't available - shouldn't happen for divider bundles
  if (bundleIndex === -1 || !dividerPage) {
    return null;
  }

  return (
    <FormAwareDividerSlide<SlideEditorBatchFormShape>
      dividerCopy={dividerPage.dividerTitle}
      customContent={dividerPage.customContent}
      slideId="slide-editor"
      dividerTitlePath={`bundles.${bundleIndex}.dividerPage.dividerTitle`}
      customContentPath={`bundles.${bundleIndex}.dividerPage.customContent`}
      highlightVariables
    />
  );
}
